<template>
  <div class="card-key">
    <div class="card-input-container">
      <van-cell-group>
        <van-field
          v-model="cardKey"
          label="卡密"
          placeholder="请输入卡密"
          :error="!!error"
          :error-message="error"
          @input="clearError"
        />
      </van-cell-group>
      <div class="tip-message">
        <van-icon name="info-o" />
        <span>链接即买即用,不支持囤货</span>
      </div>
      <div class="button-container">
        <van-button
          type="primary"
          block
          @click="submitCardKey"
          :loading="loading"
        >
          确认
        </van-button>
      </div>
    </div>

    <!-- 订单列表弹窗 -->
    <van-popup v-model="showOrderList" position="bottom" round closeable :style="{ height: '70%' }">
      <div class="order-list-popup">
        <div class="popup-title">选择要查看的订单</div>
        <div class="order-list">
          <div
            v-for="order in recentOrders"
            :key="order.orderId"
            class="order-item"
            @click="goToOrderDetail(order.orderCode)"
          >
            <div class="order-info">
              <div class="shop-name">
                <van-icon name="shop-o" />
                {{ order.shopName }}
              </div>
              <div class="goods-info">
                <span class="goods-name">商品名:{{ getGoodsNames(order) }}</span>
                <span class="goods-count">共{{ order.productCount }}件</span>
              </div>
              <div class="order-meta">
                <span class="order-time">{{ order.orderTime }}</span>
                <van-tag :type="order.orderStatus === 70 ? 'success' : 'primary'">
                  {{ getOrderStatusText(order.orderStatus) }}
                </van-tag>
              </div>
            </div>
            <van-icon name="arrow" />
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import request from "@/utils/request";

export default {
  name: "CardKey",
  data() {
    return {
      cardKey: "",
      error: "",
      loading: false,
      showOrderList: false,
      recentOrders: [],
    };
  },
  computed: {
    ...mapState(["mxAccessToken", "appConfig"]),
  },
  created() {
    // 先清空缓存的mxAccessToken和localStorage
    this.$store.dispatch("clearMxAccessToken");
    this.$store.dispatch("clearMemberCoupon");
    this.$store.dispatch("clearUuid");
    this.$store.dispatch("clearTeaType");
    this.$store.dispatch("clearWxid");
    this.$store.dispatch("clearRefreshChannel");
    // localStorage.clear(); // 一般不需要清空所有localStorage

    // 如果已经有卡密，直接跳转到首页
    if (this.mxAccessToken) {
      this.$router.push("/shop-list");
      return;
    }

    // 检查URL参数中是否有加密数据
    const encryptedData = this.$route.query.uuid;
    if (encryptedData) {
      this.cardKey = encryptedData;
      this.submitCardKey();
    }
  },
  methods: {
    ...mapActions(["setMxAccessToken", "setMemberCoupon", "setUuid", "setTeaType", "setWxid", "setRefreshChannel"]),
    clearError() {
      this.error = "";
    },
    goToOrderDetail(orderCode) {
      this.showOrderList = false;
      this.$router.push({
        path: "/order-detail",
        query: { orderCode },
      });
    },
    getGoodsNames(order) {
      if (!order.orderDetailList || order.orderDetailList.length === 0) {
        return '无商品信息';
      }
      // 获取第一个商品名称
      const firstName = order.orderDetailList[0].productName;
      // 如果只有一个商品，直接返回
      if (order.orderDetailList.length === 1) {
        return firstName;
      }
      // 如果有多个商品，返回"商品名等N件"
      return `${firstName}等${order.orderDetailList.length}件`;
    },
    getOrderStatusText(status) {
      const statusMap = {
        10: '待支付',
        20: '待接单',
        30: '制作中',
        40: '待取餐',
        50: '配送中',
        60: '待评价',
        70: '已完成',
        80: '已取消',
        90: '退款中',
        100: '已退款'
      };
      return statusMap[status] || '未知状态';
    },
    async submitCardKey() {
      if (!this.cardKey.trim()) {
        this.error = "请输入卡密";
        return;
      }
      this.loading = true;

      try {
        // 调用后端解密接口
        const response = await request.post("/encrypt/get-data", {
          uuid: this.cardKey.trim(),
        });

        if (!response) {
          throw new Error(response.message || "卡密验证失败");
        }

        const decryptedText = response.data.originalData;
        const parts = decryptedText.split("----");
        const wxid = parts[0];
        let mxAccessToken = parts[1];
        let memberCouponId = parts[2];
        let teaType = parts[3];
        const refreshChannel = parts[4] || null;
        await this.setWxid(wxid);
        await this.setMxAccessToken(mxAccessToken);
        await this.setUuid(this.cardKey.trim());
        await this.setRefreshChannel(refreshChannel);
        
        // 存储 teaType 到 Vuex (使用 commit 确保同步)
        this.$store.commit('SET_TEA_TYPE', teaType);
        
        // 从接口获取配置列表
        const configResponse = await request.get("/appConfig/list");
        if (!configResponse || !configResponse.success) {
          throw new Error("获取配置列表失败");
        }
        
        // 过滤出 is_qm === 1 的配置，并根据 type_key 匹配
        const configList = configResponse.data.filter(item => item.is_qm === 0);
        const matchedConfig = configList.find(item => item.type_key == teaType);
        
        if (!matchedConfig) {
          this.$toast.fail("未找到对应的配置，请联系客服");
          this.loading = false;
          return;
        }
        
        const defaultConfig = {
          storeId: matchedConfig.storeid,
          appid: matchedConfig.appid,
          name: matchedConfig.name,
          dataId: memberCouponId
        };
        this.$store.commit("SET_APP_CONFIG", defaultConfig);

        // 记录卡密是否已被使用
        const isCardUsed = response.data.isUsed === 1;

        // 调用订单列表接口
        try {
          const orderListResponse = await request.post("/mx/get-orders", {
            type: 0,
            page: 1,
            limit: 10,
            accessToken: mxAccessToken,
          });

          console.log('[CardKey] 订单列表响应:', orderListResponse);
          console.log('[CardKey] isCardUsed:', isCardUsed);
          console.log('[CardKey] orderListResponse.data:', orderListResponse.data);

          if (
            orderListResponse.data &&
            orderListResponse.data.list &&
            orderListResponse.data.list.length > 0
          ) {
            // 查找所有未取消且未支付的订单，并使用了相同优惠券
            const matchedOrders = orderListResponse.data.list.filter((order) => {
              // orderStatus: 10=待支付, 80=已取消, 70=已完成
              const isNotCancelled = order.orderStatus !== 80 && order.orderStatus !== 10;
              console.log('[CardKey] 订单过滤:', order.orderCode, 'status:', order.orderStatus, 'isNotCancelled:', isNotCancelled);
              return isNotCancelled
            });
            
            console.log('[CardKey] matchedOrders.length:', matchedOrders.length, 'isCardUsed:', isCardUsed);
            
            if (matchedOrders.length > 0 && isCardUsed) {
              // 只有在卡密已被使用的情况下，才显示订单列表
              this.recentOrders = matchedOrders.slice(0, 10);
              this.loading = false;
              
              // 提示用户该链接只能查订单
              this.$toast({
                message: '该链接已被使用过，仅提供查订单功能。如需下单请联系客服处理',
                duration: 3000,
                onClose: () => {
                  this.showOrderList = true;
                }
              });
              
              return; // 显示订单列表，不继续执行后续代码
            }
            // 如果卡密未使用，即使有订单，也继续正常流程（不显示订单列表）
          }
        } catch (orderError) {
          console.error("获取订单列表失败：", orderError);
        }

        // 如果卡密已被使用，且没有找到订单（上面的 return 没有执行），则提示用户
        if (isCardUsed) {
          this.$toast.fail("该卡密已使用，请联系客服更换");
          this.loading = false;
          return;
        }

        let list = await request.post("/mx/get-coupons", {
          couponStatus: 0,
          pageNumber: 1,
          pageSize: 10,
          accessToken: this.mxAccessToken,
        });

        console.log("list",list);
        
        // 优惠券数据在 list.data.list 中（根据蜜雪接口返回结构）
        const couponList = list.data?.list || [];

        let memberCouponObj = couponList.find(
          (item) => item.couponRuleId === memberCouponId
        );

        console.log(memberCouponObj);
        

        if (!memberCouponObj) {
          this.$toast("该账号无对应免单卷，请联系客服售后处理");
          this.loading = false;
          return;
        }
        await this.setMemberCoupon(memberCouponObj);

        this.$router.push("/shop-list");
      } catch (error) {
        console.error("卡密验证失败:", error);
        this.error = error.message || "卡密验证失败";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-key {
  min-height: 100vh;
  background-color: #f7f8fa;
  display: flex;
  align-items: center;
  padding: 20px;
}

.card-input-container {
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.button-container {
  margin-top: 20px;
}

.tip-message {
  margin-top: 12px;
  padding: 8px 12px;
  background-color: #f0f9ff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #666;

  .van-icon {
    color: #1989fa;
    margin-right: 4px;
    font-size: 14px;
  }
}

.order-list-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f7f8fa;

  .popup-title {
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    padding: 16px;
    background-color: #fff;
    border-bottom: 1px solid #f5f5f5;
  }

  .order-list {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
  }

  .order-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #fff;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    &:active {
      background-color: #f5f5f5;
    }

    .order-info {
      flex: 1;

      .shop-name {
        font-size: 15px;
        font-weight: 600;
        color: #323233;
        margin-bottom: 10px;
        display: flex;
        align-items: center;

        .van-icon {
          color: #ee0a24;
          margin-right: 6px;
          font-size: 16px;
        }
      }

      .goods-info {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        .goods-name {
          font-size: 13px;
          color: #646566;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .goods-count {
          font-size: 12px;
          color: #969799;
          margin-left: 8px;
        }
      }

      .order-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .order-time {
          font-size: 12px;
          color: #969799;
        }
      }
    }

    > .van-icon {
      color: #969799;
      font-size: 16px;
      margin-left: 8px;
    }
  }
}
</style> 